import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  createDocumentVersion,
  updateDocumentVersion,
  listDocumentVersions,
  getDocumentVersion,
  DocumentVersion,
} from '@/lib/api/client'
import queryClient from '@/queryClient'
import MUTATION_KEYS from './mutationKeys'
import QUERY_KEYS from './queryKeys'
import { useToast } from '@/components/ui/use-toast'
import CustomError from '@/lib/api/CustomError'
import { useMemo } from 'react'
import { useJwtToken } from '@/external/onshape/hooks/useJwtToken'
import { getUserByIdQuery } from '@/services/hooks/users'

type mutationDocumentVersionProps = {
  onSuccess?: (documentVersion?: DocumentVersion) => void
}

export const useCreateDocumentVersion = (
  props?: mutationDocumentVersionProps,
) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const jwtToken = useJwtToken()

  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_DOCUMENT_VERSION],
    mutationFn: async ({
      documentId,
      documentVersionId,
      documentVersionNumber,
      comment,
      name,
    }: {
      documentId: string
      documentVersionId?: string
      documentVersionNumber?: number
      comment?: string
      name?: string
    }) => {
      const nextName = 'Unpublished'

      if (documentVersionId && documentVersionNumber) {
        const currentName = name || `V${documentVersionNumber}`
        await updateDocumentVersion(
          documentVersionId,
          comment || '',
          currentName,
          jwtToken,
        )
      }

      const documentVersion = await createDocumentVersion(
        documentId,
        '',
        nextName,
        jwtToken,
      )
      const creator = await getUserByIdQuery(documentVersion.creator)
      return {
        ...documentVersion,
        creator,
        jwtToken,
      }
    },
    onSuccess: (documentVersion) => {
      queryClient.setQueryData(
        [
          QUERY_KEYS.DOCUMENT_VERSIONS,
          { documentId: documentVersion.document },
        ],
        (documentVersions: Array<DocumentVersion> | undefined) =>
          documentVersions
            ? [...documentVersions, documentVersion]
            : [documentVersion],
      )
      queryClient.setQueryData(
        [QUERY_KEYS.DOCUMENT_VERSION, { documentVersion: documentVersion?.id }],
        documentVersion,
      )
      if (props?.onSuccess) props.onSuccess(documentVersion)
    },
    onError: (error) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useListDocumentVersions = ({
  documentId,
}: {
  documentId?: string | null
}) => {
  const { toast } = useToast()
  return useQuery({
    enabled: Boolean(documentId),
    queryKey: [QUERY_KEYS.DOCUMENT_VERSIONS, { documentId }],
    queryFn: async () => {
      if (!documentId) return []
      try {
        const documentVersions = await listDocumentVersions(documentId)
        return await Promise.all(
          documentVersions.map(async (dv) => ({
            ...dv,
            creator: await getUserByIdQuery(dv.creator),
          })),
        )
      } catch (e: any) {
        if (e instanceof CustomError) {
          toast({
            title: 'Error',
            description: e.message,
            variant: 'destructive',
          })
        }
        throw e
      }
    },
  })
}

export const listDocumentVersionsQuery = ({
  documentId,
}: {
  documentId: string
}) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.DOCUMENT_VERSIONS, { documentId }],
    queryFn: () => listDocumentVersions(documentId),
  })
}

export const getLatestDocumentVersion = async ({
  documentId,
}: {
  documentId: string
}) => {
  const documentVersionsResp = documentId
    ? await listDocumentVersions(documentId)
    : []

  const documentVersions = documentVersionsResp.sort(
    (a, b) => (b.version_number || 0) - (a.version_number || 0),
  )

  const latestVersion =
    documentVersions.length > 0 ? documentVersions[0] : undefined

  if (!latestVersion) {
    throw new Error('No CAD versions found')
  }

  if (!latestVersion.id) {
    throw new Error('No CAD version ID found')
  }

  return latestVersion
}

const getDocumentVersionQueryFn = async ({
  documentVersionId,
  toast,
}: {
  documentVersionId?: string | null
  toast?: (args: any) => void
}) => {
  if (!documentVersionId) return {}
  try {
    const documentVersion = await getDocumentVersion(documentVersionId)

    if (documentVersion) {
      const documentId = documentVersion.document
      queryClient.setQueryData(
        [QUERY_KEYS.DOCUMENT_VERSIONS, { documentId }],
        (documentVersions: Array<DocumentVersion> | undefined) => {
          if (documentVersions) {
            const documentVersionExists = !!documentVersions.find(
              (currentDocumentVersion) =>
                currentDocumentVersion.id === documentVersionId,
            )
            return documentVersionExists
              ? documentVersions.map((currentDocumentVersion) =>
                  currentDocumentVersion.id === documentVersion.id
                    ? documentVersion
                    : currentDocumentVersion,
                )
              : [...documentVersions, documentVersion]
          }
        },
      )
    }

    return documentVersion
  } catch (e: any) {
    if (e instanceof CustomError && toast) {
      toast({
        title: 'Error',
        description: e.message,
        variant: 'destructive',
      })
    }
    throw e
  }
}

export const useGetDocumentVersion = ({
  documentVersionId,
}: {
  documentVersionId?: string | null
}) => {
  const { toast } = useToast()
  return useQuery({
    enabled: !!documentVersionId,
    queryKey: [QUERY_KEYS.DOCUMENT_VERSION, { documentVersionId }],
    queryFn: async () =>
      getDocumentVersionQueryFn({
        documentVersionId,
        toast,
      }),
  })
}

export const getDocumentVersionQuery = ({
  documentVersionId,
}: {
  documentVersionId?: string | null
}) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.DOCUMENT_VERSION, { documentVersionId }],
    queryFn: () => getDocumentVersionQueryFn({ documentVersionId }),
  })
}

export const useGetLatestDocumentVersion = ({
  documentId,
}: {
  documentId?: string | null
}) => {
  const { data: documentVersions, isLoading } = useListDocumentVersions({
    documentId,
  })

  const sortedDocumentVersions = useMemo(
    () =>
      (documentVersions || []).sort(
        (a, b) => (b?.version_number || 0) - (a?.version_number || 0),
      ),
    [documentVersions],
  )

  const latestVersion = useMemo(
    () =>
      sortedDocumentVersions.length > 0 ? sortedDocumentVersions[0] : undefined,
    [sortedDocumentVersions],
  )

  return {
    data: latestVersion,
    isLoading,
  }
}

export { QUERY_KEYS, MUTATION_KEYS }
